import type {
  NFLGameInputs,
  NFLPlayerInputs,
  NFLTeamInputs,
  OddsResponse,
  ProvidedOverride,
  RequestSimulationRunResponse,
  SimulationRun
} from '@library/types/simulations';
import { DateTime } from 'luxon';
import type { GameData } from '@library/types/games';
import type { LeagueTeamPlayer } from '@library/types/player';
import { partition } from 'ramda';

export const getSimulationResponse = ({
  game = undefined,
  gamePlayers = []
}: {
  game?: GameData;
  gamePlayers?: Array<LeagueTeamPlayer>;
}): SimulationRun => {
  const [homeTeamPlayers, awayTeamPlayers] = partition(
    (a) => a.team_name === game?.home_team_name,
    gamePlayers
  );

  const homeTeamPlayerInputs: Array<NFLPlayerInputs> = homeTeamPlayers.map(
    (player) => ({
      pass_attempt_share: 0, // min 0.0, max 1.0
      is_qb: false,
      carry_share: 0, // min 0.0, max 1.0
      catch_adjustment: 0, // min -0.25, max 0.25
      yards_per_carry: 0, // min 0.0, max 10.0
      short_yardage_carry_share: 0, // min 0.0, max 1.0
      rush_td_rate: 0, // min 0.0, max 1.0
      rush_td_adjustment: 0, // min -1.0, max 1.0
      target_rate: 0, // min 0.0, max 1.0
      target_adjustment: 0, // min -0.5, max 0.5
      average_depth_of_target: 0, // min -2.0, max 30.0
      yards_per_target: 0, // min 0.0, max 20.0
      yards_per_completion: 0, // min 0.0, max 30.0
      catch_rate: 0, // min 0.0, max 1.0
      rec_td_rate: 0, // min -1.0, max 1.0
      rec_td_adjustment: 0, // min -1.0, max 1.0
      pass_play_playing_time: 0, // min 0.0, max 1.0
      rush_play_playing_time: 0, // min 0.0, max 1.0
      pass_play_blowout_playing_time: 0, // min 0.0, max 1.0
      rush_play_blowout_playing_time: 0, // min 0.0, max 1.0
      route_run_rate: 0, // min 0.0, max 1.0,
      player_name: player.name,
      sportradar_player_id: player.foreign_id,
      prizepicks_player_id: player.foreign_id
    })
  );

  const awayTeamPlayerInputs: Array<NFLPlayerInputs> = awayTeamPlayers.map(
    (player) => ({
      pass_attempt_share: 0, // min 0.0, max 1.0
      is_qb: false,
      carry_share: 0, // min 0.0, max 1.0
      catch_adjustment: 0, // min -0.25, max 0.25
      yards_per_carry: 0, // min 0.0, max 10.0
      short_yardage_carry_share: 0, // min 0.0, max 1.0
      rush_td_rate: 0, // min 0.0, max 1.0
      rush_td_adjustment: 0, // min -1.0, max 1.0
      target_rate: 0, // min 0.0, max 1.0
      target_adjustment: 0, // min -0.5, max 0.5
      average_depth_of_target: 0, // min -2.0, max 30.0
      yards_per_target: 0, // min 0.0, max 20.0
      yards_per_completion: 0, // min 0.0, max 30.0
      catch_rate: 0, // min 0.0, max 1.0
      rec_td_rate: 0, // min -1.0, max 1.0
      rec_td_adjustment: 0, // min -1.0, max 1.0
      pass_play_playing_time: 0, // min 0.0, max 1.0
      rush_play_playing_time: 0, // min 0.0, max 1.0
      pass_play_blowout_playing_time: 0, // min 0.0, max 1.0
      rush_play_blowout_playing_time: 0, // min 0.0, max 1.0
      route_run_rate: 0, // min 0.0, max 1.0,
      player_name: player.name,
      sportradar_player_id: player.foreign_id,
      prizepicks_player_id: player.foreign_id
    })
  );

  return {
    data: {
      id: '1',
      type: 'type',
      attributes: {
        simulation_run_id: 'id',
        simulation_run_name: 'name',
        simulation_run_status: 'SIMULATION_COMPLETED',
        simulation_run_type: 'CANDIDATE',
        game_id: game?.id || 'game_id',
        game_start_time: game?.scheduled_at || 'game_start_time',
        error_message: 'error_message',
        simulation_run_inputs: {
          game_inputs: {
            temperature: 70, // min -20, max 120
            wind_speed: 5, // min 0, max 50
            grass_field: true,
            dome_weather: true,
            total_points: 50, // min 0
            home_team_spread: -9.5
          },
          team_inputs: [
            {
              yards_per_pass_attempt: 0,
              yards_per_rush_attempt: 0,
              pass_attempt_rate: 0,
              pass_completion_prob_adjustment: 0,
              pass_completion_rate: 0,
              pass_play_prob_adjustment: 0,
              pass_touchdown_prob_adjustment: 0,
              rush_touchdown_prob_adjustment: 0,
              pass_yardage_adjustment: 0,
              rush_yardage_adjustment: 0,
              sack_rate: 0,
              team_name: game?.home_team_name || 'home_team_name',
              home_team: true,
              prizepicks_team_id: game?.home_team_id || 'home_team_id',
              sportradar_team_id: game?.home_team_id || 'home_team_id'
            },
            {
              yards_per_pass_attempt: 0,
              yards_per_rush_attempt: 0,
              pass_attempt_rate: 0,
              pass_completion_prob_adjustment: 0,
              pass_completion_rate: 0,
              pass_play_prob_adjustment: 0,
              pass_touchdown_prob_adjustment: 0,
              rush_touchdown_prob_adjustment: 0,
              pass_yardage_adjustment: 0,
              rush_yardage_adjustment: 0,
              sack_rate: 0,
              team_name: game?.away_team_name || 'away_team_name',
              home_team: true,
              prizepicks_team_id: game?.away_team_id || 'away_team_id',
              sportradar_team_id: game?.away_team_id || 'away_team_id'
            }
          ],
          player_inputs: [
            {
              players: homeTeamPlayerInputs,
              prizepicks_team_id: game?.home_team_id || 'home_team_id',
              sportradar_team_id: game?.home_team_id || 'home_team_id',
              team_name: game?.home_team_name || 'home_team_name',
              home_team: true
            },
            {
              players: awayTeamPlayerInputs,
              prizepicks_team_id: game?.away_team_id || 'away_team_id',
              sportradar_team_id: game?.away_team_id || 'away_team_id',
              team_name: game?.away_team_name || 'away_team_name',
              home_team: false
            }
          ],
          removed_players: []
        }
      }
    }
  };
};

export const getSimulationsResponse = ({
  gameId,
  simulationId,
  simulationName
}: {
  gameId: string;
  simulationId: string;
  simulationName: string;
}): Array<RequestSimulationRunResponse> => [
  {
    attributes: {
      game_id: gameId,
      game_start_time: DateTime.now().toISO(),
      simulation_run_id: simulationId,
      simulation_run_name: simulationName,
      simulation_run_type: 'CANDIDATE',
      simulation_run_status: 'SIMULATION_COMPLETED'
    },
    id: '1',
    type: 'simulation_run'
  }
];

export const getOddsResponse = ({
  gameId,
  allMarkets,
  players,
  simulationId
}: {
  gameId: string;
  allMarkets: Array<{
    league_id: string;
    market: string;
    market_id: string;
    stat_type_id: number;
  }>;
  players: {
    foreignId: string;
    playerId: string;
    playerName: string;
    teamName: string;
  }[];
  simulationId: string;
}): OddsResponse => ({
  game_id: gameId,
  players: players.map((player) => ({
    prizepicks_player_id: player.playerId,
    sportradar_player_id: player.foreignId,
    player_name: player.playerName,
    prizepicks_team_id: null,
    sportradar_team_id: null,
    team_name: player.teamName,
    game_id: gameId,
    markets: allMarkets.map((market) => ({
      market: market.market,
      stat_type_id: market.stat_type_id,
      median_lines: {
        simulation_runs: [
          {
            simulation_run_id: simulationId,
            simulation_run_name: simulationId,
            simulation_run_type: 'CANDIDATE',
            line: String(Math.floor(Math.random() * 1000) / 10),
            odds: {
              devigged: {
                implied_prob: String(Math.random()),
                decimal_odds: String(Math.random()),
                american_odds: Math.random()
              },
              vigged: {
                implied_prob: String(Math.random()),
                decimal_odds: String(Math.random()),
                american_odds: Math.random()
              }
            }
          }
        ]
      },

      legacy_consensus_odds: {
        line: String(Math.floor(Math.random() * 1000) / 10),
        odds: {
          devigged: {
            implied_prob: String(Math.random()),
            decimal_odds: String(Math.random()),
            american_odds: Math.random()
          },
          vigged: {
            implied_prob: String(Math.random()),
            decimal_odds: String(Math.random()),
            american_odds: Math.random()
          }
        }
      },

      prizepicks_currently_posted: {
        line: String(Math.floor(Math.random() * 1000) / 10),
        odds: {
          devigged: {
            implied_prob: String(Math.random()),
            decimal_odds: String(Math.random()),
            american_odds: Math.random()
          },
          vigged: {
            implied_prob: String(Math.random()),
            decimal_odds: String(Math.random()),
            american_odds: Math.random()
          }
        }
      },

      third_parties: [],

      alt_lines: []
    }))
  }))
});

export const PLAYER_OVERRIDE_TYPES: Array<
  ProvidedOverride<keyof NFLPlayerInputs>
> = [
  {
    description: 'Indicator of whether the player is the QB in this game',
    value: 'is_qb',
    label: 'Is Starting QB',
    type: 'bool'
  },
  {
    description:
      "The % of the team's pass attempts the player will perform. Should sum up to 1 across all players in the players array.",
    value: 'pass_attempt_share',
    label: 'Pass Attempt Share (%)',
    minimum: 0.0,
    maximum: 100.0,
    format: 'float',
    type: 'number',
    percentage: true
  },
  {
    description:
      "% of the team's carries performed by the player. should sum up to 1 across all players in the players array.",
    value: 'carry_share',
    label: 'Carry Share (%)',
    minimum: 0.0,
    maximum: 100.0,
    format: 'float',
    type: 'number',
    percentage: true
  },
  {
    value: 'catch_adjustment',
    label: 'Catch Adjustment (%)',
    type: 'number',
    format: 'float',
    description:
      'Additive adjustment on the catch probability for the player on each pass play in the simulation.',
    minimum: -25.0,
    maximum: 25.0,
    percentage: true
  },
  {
    description: 'The average yards per carry performed by the player.',
    value: 'yards_per_carry',
    label: 'Yards Per Carry',
    minimum: 0.0,
    maximum: 10.0,
    format: 'float',
    type: 'number'
  },
  {
    description:
      'Percent of player carries that are within 3 yards of the line to gain or goal line',
    value: 'short_yardage_carry_share',
    label: 'Short Yardage Carry Share (%)',
    type: 'number',
    format: 'float',
    minimum: 0.0,
    maximum: 100.0,
    percentage: true
  },
  {
    value: 'rush_td_rate',
    label: 'Rush TD Rate (%)',
    type: 'number',
    format: 'float',
    description: "Percent of player's carries that result in a touchdown.",
    minimum: 0.0,
    maximum: 100.0,
    percentage: true
  },
  {
    value: 'rush_td_adjustment',
    label: 'Rush TD Adjustment (%)',
    type: 'number',
    format: 'float',
    description:
      'Additive adjustment on the probability that a player carries the ball, given that a touchdown was scored on the run play. NOTE: Can result in an error if this adjustment pushes the probability outside of [0.0, 1.0].',
    minimum: -100.0,
    maximum: 100.0,
    percentage: true
  },
  {
    value: 'target_rate',
    label: 'Target Rate (%)',
    type: 'number',
    format: 'float',
    description:
      'Percent of the time the player is targeted during a pass play.',
    minimum: 0.0,
    maximum: 100.0,
    percentage: true
  },
  {
    value: 'target_adjustment',
    label: 'Target Adjustment (%)',
    type: 'number',
    format: 'float',
    description:
      'Additive adjustment on the target probability for the player on each pass play in the simulation.',
    minimum: -50.0,
    maximum: 50.0,
    percentage: true
  },
  {
    value: 'average_depth_of_target',
    label: 'Average Depth of Target',
    type: 'number',
    format: 'float',
    description:
      "Average downfield distance of the quarterback's passes (in yards) when this player is targeted.",
    minimum: -2.0,
    maximum: 30.0
  },
  {
    value: 'yards_per_target',
    label: 'Yards Per Target',
    type: 'number',
    format: 'float',
    description: 'Average yards gained when the player is targeted',
    minimum: 0.0,
    maximum: 20.0
  },
  {
    value: 'yards_per_completion',
    label: 'Yards Per Completion',
    type: 'number',
    format: 'float',
    description:
      'Average yards gained when the player completes the pass (e.g. gets a reception)',
    minimum: 0.0,
    maximum: 30.0
  },
  {
    value: 'catch_rate',
    label: 'Catch Rate (%)',
    type: 'number',
    format: 'float',
    description: 'Percent of time the player catches the ball when targeted',
    minimum: 0.0,
    maximum: 100.0,
    percentage: true
  },
  {
    value: 'rec_td_rate',
    label: 'Rec TD Rate (%)',
    type: 'number',
    format: 'float',
    description: "Percent of player's targets that result in a touchdown.",
    minimum: 0.0,
    maximum: 100.0,
    percentage: true
  },
  {
    value: 'rec_td_adjustment',
    label: 'Rec TD Adjustment (%)',
    type: 'number',
    format: 'float',
    description:
      'Additive adjustment on the probability that a player is targeted for a pass, given that a touchdown was scored on the pass play. NOTE: Can result in an error if this adjustment pushes the probability outside of [0.0, 1.0].',
    minimum: -100.0,
    maximum: 100.0,
    percentage: true
  },
  {
    value: 'pass_play_playing_time',
    label: 'Pass Play Playing Time (%)',
    type: 'number',
    format: 'float',
    description: 'The percent of pass plays the player is on the field for.',
    minimum: 0.0,
    maximum: 100.0,
    percentage: true
  },
  {
    value: 'rush_play_playing_time',
    label: 'Rush Play Playing Time (%)',
    type: 'number',
    format: 'float',
    description: 'The percent of run plays the player is on the field for.',
    minimum: 0.0,
    maximum: 100.0,
    percentage: true
  },
  {
    value: 'pass_play_blowout_playing_time',
    label: 'Pass Play Blowout Playing Time (%)',
    type: 'number',
    format: 'float',
    description:
      'The percent of pass plays the player is on the field for during a blowout game.',
    minimum: 0.0,
    maximum: 100.0,
    percentage: true
  },
  {
    value: 'rush_play_blowout_playing_time',
    label: 'Rush Play Blowout Playing Time (%)',
    type: 'number',
    format: 'float',
    description:
      'The percent of run plays the player is on the field for during a blowout game.',
    minimum: 0.0,
    maximum: 100.0,
    percentage: true
  },
  {
    value: 'route_run_rate',
    label: 'Route Run Rate (%)',
    type: 'number',
    format: 'float',
    description:
      'Total number of routes run by the player, over total pass play snaps.',
    minimum: 0.0,
    maximum: 100.0,
    percentage: true
  }
];

export const GAME_OVERRIDE_TYPES: Array<ProvidedOverride<keyof NFLGameInputs>> =
  [
    {
      default: 73,
      description:
        'The predicted temperature in fahrenheit at the start of game time',
      label: 'Temperature',
      minimum: -20,
      maximum: 120,
      type: 'integer',
      value: 'temperature'
    },
    {
      default: 5,
      description: 'The predicted wind speed in mph at the start of game time',
      label: 'Wind Speed',
      minimum: 0,
      maximum: 50,
      type: 'integer',
      value: 'wind_speed'
    },
    {
      default: true,
      description: 'Is the game played on a grass field?',
      value: 'grass_field',
      label: 'Grass Field?',
      type: 'bool'
    },
    {
      default: true,
      description: 'Is the game played in a weather-controlled dome?',
      value: 'dome_weather',
      label: 'Dome?',
      type: 'bool'
    },
    {
      default: 100,
      description: 'The predicted total points for the game',
      label: 'Total Points',
      minimum: 0,
      type: 'integer',
      value: 'total_points'
    },
    {
      default: -9.5,
      description: 'The Home Team Spread for this game',
      format: 'float',
      label: 'Home Team Spread',
      type: 'number',
      value: 'home_team_spread'
    }
  ];

export const TEAM_OVERRIDE_TYPES: Array<ProvidedOverride<keyof NFLTeamInputs>> =
  // NOTE: As per DSE, all team inputs that aren't adjustments should be removed for now
  // in order to streamline Simulations usage for Game Ops
  [
    // {
    //   description: 'The predicted yardage per pass attempt by this team',
    //   format: 'float',
    //   label: 'Yards Per Pass Attempt',
    //   minimum: 0.0,
    //   maximum: 10.0,
    //   type: 'number',
    //   value: 'yards_per_pass_attempt'
    // },
    // {
    //   type: 'number',
    //   format: 'float',
    //   description:
    //     'Average yards expected to be gained per rush attempt. Does not include sack yards.',
    //   label: 'Yards Per Rush Attempt',
    //   minimum: 0.0,
    //   maximum: 10.0,
    //   value: 'yards_per_rush_attempt'
    // },
    // {
    //   type: 'number',
    //   format: 'float',
    //   description:
    //     'Indicator of the % of time that the team will go for a pass play (rather than a rush play).',
    //   minimum: 0.0,
    //   maximum: 100.0,
    //   label: 'Pass Attempt Rate (%)',
    //   value: 'pass_attempt_rate'
    // },
    // {
    //   description: 'Rate at which pass attempts will be completed',
    //   value: 'pass_completion_rate',
    //   label: 'Pass Completion Rate (%)',
    //   type: 'number',
    //   format: 'float',
    //   minimum: 0.0,
    //   maximum: 100.0
    // },
    {
      type: 'number',
      format: 'float',
      description:
        'Adjustment of how much more % we should add to the probability of a team attempting a pass during uncertain pass/play situations (pass_play_prob > 0.2 and rush_play_prob > 0.2). NOTE: Will throw an error if the adjustment is larger than either the pass_play_prob or rush_play_prob of a given play.',
      minimum: -100.0,
      maximum: 100.0,
      value: 'pass_play_prob_adjustment',
      label: 'Pass Play Prob Adjustment (%)'
    },
    {
      type: 'number',
      format: 'float',
      description:
        'Adjustment of the average number of yards to add to each pass play.',
      minimum: -20.0,
      maximum: 20.0,
      label: 'Pass Yardage Adjustment',
      value: 'pass_yardage_adjustment'
    },
    {
      type: 'number',
      format: 'float',
      description:
        'Adjustment of how much more % we should add to the probability of a team completing a pass during uncertain pass/completion situations (pass_completion_prob > 0.2). NOTE: Will throw an error if the adjustment is larger than either the pass_completion_prob of a given play.',
      minimum: -20.0,
      maximum: 20.0,
      label: 'Pass Completion Prob Adjustment (%)',
      value: 'pass_completion_prob_adjustment'
    },
    {
      type: 'number',
      format: 'float',
      description: 'Adjusts how likely a team is to run for a TD.',
      minimum: -20.0,
      maximum: 20.0,
      label: 'Rush TD Prob Adjustment (%)',
      value: 'rush_touchdown_prob_adjustment'
    },
    {
      type: 'number',
      format: 'float',
      description: 'Adjusts how likely a team is to pass for a TD.',
      minimum: -20.0,
      maximum: 20.0,
      label: 'Pass TD Prob Adjustment (%)',
      value: 'pass_touchdown_prob_adjustment'
    },
    {
      type: 'number',
      format: 'float',
      description:
        'Adjustment of the average number of yards to add to each run play.',
      minimum: -5.0,
      maximum: 5.0,
      label: 'Rush Yardage Adjustment',
      value: 'rush_yardage_adjustment'
    }
    // {
    //   type: 'number',
    //   format: 'float',
    //   description: 'Rate at which plays end in a sack."',
    //   minimum: 0.0,
    //   maximum: 100.0,
    //   label: 'Sack Rate (%)',
    //   value: 'sack_rate'
    // }
  ];
